import { Box, Text } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { useCallback, useEffect, useRef, useState } from 'react';
import LinkButton from '~/components/LinkButton';
import { useOnWindowResize } from '~/hooks/useOnWindowResize';
import { ISearchContentResult } from '~/searchApi';
import { formatDate } from '~/utils';

import { HighlightSearchTerm } from '../HighlightSearchTerm';

export interface ContentCaptionProps {
  content: ISearchContentResult;
}

const ClampBox = styled(Box, {
  shouldForwardProp: (propName) => !['isClamped', 'lines'].includes(propName),
})`
  display: ${(props: { isClamped: boolean; lines: number }) =>
    props.isClamped ? '-webkit-box' : 'block'};
  -webkit-line-clamp: ${(props: { isClamped: boolean; lines: number }) =>
    props.isClamped ? props.lines : 'unset'};
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const clampLines = 3;

export function ContentCaption({ content }: ContentCaptionProps) {
  const ref = useRef({ scrollHeight: 0, clientHeight: 0 });
  const [isClamped, setIsClamped] = useState(true);
  const [isClampedNeeded, setIsClampedNeeded] = useState(false);

  // check if it's clamping by checking the element's overflow
  const checkClampOverflow = useCallback(() => {
    setIsClamped(true);
    const { scrollHeight = 0, clientHeight = 0 } = ref.current || {};
    setIsClampedNeeded(scrollHeight > clientHeight);
  }, []);

  useEffect(() => {
    checkClampOverflow();
  }, [checkClampOverflow, ref?.current?.scrollHeight]);

  useOnWindowResize(checkClampOverflow);

  const shouldRenderMoreButton = isClampedNeeded && isClamped;
  const shouldRenderLessButton = isClampedNeeded && !isClamped;

  return (
    <>
      <Text fontSize="md" color="primary.300" marginBottom={4}>
        Posted{' '}
        {formatDate(
          content.publishedAt ||
            content.createdTime ||
            (content as any).created_at,
        )}
      </Text>

      {Boolean(content.caption?.length) && (
        <Box
          position="relative"
          as="blockquote"
          color="primary.400"
          fontSize="lg"
          fontWeight={400}
          marginTop={9}
          paddingBottom={9}
        >
          <ClampBox ref={ref} lines={clampLines} isClamped={isClamped}>
            <Text
              position="absolute"
              top={0}
              left={0}
              as="span"
              color="primary.100"
              fontSize="50px"
              verticalAlign="bottom"
              lineHeight={0}
            >
              “
            </Text>
            <Text>
              <HighlightSearchTerm>{content.caption}</HighlightSearchTerm>
            </Text>
            <Text
              position="absolute"
              bottom={4}
              right={0}
              as="span"
              color="primary.100"
              fontSize="50px"
              verticalAlign="top"
              marginLeft="auto"
              lineHeight={0}
            >
              ”
            </Text>
          </ClampBox>
          {shouldRenderMoreButton && (
            <LinkButton
              position="absolute"
              bottom={0}
              left={0}
              color="primary.500"
              fontSize="sm"
              onClick={() => setIsClamped(false)}
            >
              Read more
            </LinkButton>
          )}
          {shouldRenderLessButton && (
            <LinkButton
              position="absolute"
              bottom={0}
              left={0}
              color="primary.500"
              fontSize="sm"
              onClick={() => setIsClamped(true)}
            >
              Show less
            </LinkButton>
          )}
        </Box>
      )}
    </>
  );
}
